/**
 * Admin: Workflow Metadata
 *
 * Wraps the `/api/workflowMetadata` endpoint:
 *   GET    ?action=summary           (default; aggregates over a time window)
 *   GET    ?action=lookup            (rows by eventSourceId or source LIKE)
 *   GET    ?action=orphan-entities   (entities with no EntityEventSources)
 *   DELETE ?action=delete-source&eventSourceId=&confirm=true
 *   DELETE ?action=delete-failed&confirm=true
 *   DELETE ?action=delete-orphan-entities&confirm=true
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  Search,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  getWorkflowSummary,
  lookupWorkflowRuns,
  getOrphanEntities,
  deleteEventSourceCascade,
  deleteFailedSources,
  deleteOrphanEntities,
  previewSourceCleanup,
  runSourceCleanup,
  saveLastRun,
  loadLastRun,
  type WorkflowSummaryResponse,
  type WorkflowLookupResponse,
  type WorkflowOrphanEntitiesResponse,
  type WorkflowDeleteSourceResponse,
  type WorkflowDeleteFailedResponse,
  type WorkflowDeleteOrphanEntitiesResponse,
  type SourceCleanupReport,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  RunningIndicator,
  ConfirmDialog,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  SampleTable,
  ButtonRow,
  Field,
  Label,
  Input,
  InlineLabel,
} from '../components/admin/AdminUI';

const ACTION_DELETE_SOURCE = 'workflowMetadata.deleteSource';
const ACTION_DELETE_FAILED = 'workflowMetadata.deleteFailed';
const ACTION_DELETE_ORPHANS = 'workflowMetadata.deleteOrphanEntities';

const DEFAULT_FAILURE_PATTERN = 'fail,error';

type DialogKind =
  | null
  | { kind: 'delete-source'; eventSourceId: number; sourceLabel?: string }
  | { kind: 'delete-failed' }
  | { kind: 'delete-orphans' };

// ---------------------------------------------------------------------------
// Date helpers — translate between ISO 8601 (API) and the value strings used
// by `<input type="datetime-local">` (which are local-time, no TZ).
// ---------------------------------------------------------------------------

function isoNow(): Date {
  return new Date();
}

function isoNDaysAgo(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

/** Convert a Date to a `<input type="datetime-local">` value (local time). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Convert a local-time `<input type="datetime-local">` value to an ISO UTC string. */
function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v); // interpreted as local time
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function fmtDate(s?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function isFailureStatus(status: string | undefined, patterns: string[]): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return patterns.some((p) => p && s.includes(p.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AdminWorkflowMetadataPage: React.FC = () => {
  const { userInfo } = useAuth();

  // ---- Time window + failure pattern controls ---------------------------
  const [startTime, setStartTime] = useState<string>(() =>
    toLocalInput(isoNDaysAgo(3)),
  );
  const [endTime, setEndTime] = useState<string>(() => toLocalInput(isoNow()));
  const [failurePattern, setFailurePattern] = useState<string>(
    DEFAULT_FAILURE_PATTERN,
  );

  const setQuickRange = (days: number) => {
    setStartTime(toLocalInput(isoNDaysAgo(days)));
    setEndTime(toLocalInput(isoNow()));
  };

  // ---- Summary state -----------------------------------------------------
  const [summary, setSummary] = useState<WorkflowSummaryResponse | null>(null);
  const [summaryLoadedAt, setSummaryLoadedAt] = useState<Date | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // ---- Lookup state ------------------------------------------------------
  const [lookupSourceId, setLookupSourceId] = useState<string>('');
  const [lookupSource, setLookupSource] = useState<string>('');
  const [lookupAllTime, setLookupAllTime] = useState<boolean>(false);
  const [lookup, setLookup] = useState<WorkflowLookupResponse | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // ---- Orphan entities state --------------------------------------------
  const [orphans, setOrphans] = useState<WorkflowOrphanEntitiesResponse | null>(
    null,
  );
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansError, setOrphansError] = useState<string | null>(null);

  // ---- Single-source delete (free-form id) ------------------------------
  const [deleteIdInput, setDeleteIdInput] = useState<string>('');

  // ---- Mutation state ---------------------------------------------------
  const [running, setRunning] = useState(false);
  const [opStartedAt, setOpStartedAt] = useState<number | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [opKind, setOpKind] = useState<
    null | 'delete-source' | 'delete-failed' | 'delete-orphans' | 'source-cleanup'
  >(null);
  const [deleteSourceResult, setDeleteSourceResult] =
    useState<WorkflowDeleteSourceResponse | null>(null);
  const [deleteFailedResult, setDeleteFailedResult] =
    useState<WorkflowDeleteFailedResponse | null>(null);
  const [deleteOrphansResult, setDeleteOrphansResult] =
    useState<WorkflowDeleteOrphanEntitiesResponse | null>(null);
  const [cleanupResult, setCleanupResult] = useState<SourceCleanupReport | null>(null);

  const [dialog, setDialog] = useState<DialogKind>(null);

  // ---- Last-run records --------------------------------------------------
  const [lastDeleteSource, setLastDeleteSource] =
    useState<LastRunRecord<WorkflowDeleteSourceResponse> | null>(null);
  const [lastDeleteFailed, setLastDeleteFailed] =
    useState<LastRunRecord<WorkflowDeleteFailedResponse> | null>(null);
  const [lastDeleteOrphans, setLastDeleteOrphans] =
    useState<LastRunRecord<WorkflowDeleteOrphanEntitiesResponse> | null>(null);

  useEffect(() => {
    setLastDeleteSource(loadLastRun(ACTION_DELETE_SOURCE));
    setLastDeleteFailed(loadLastRun(ACTION_DELETE_FAILED));
    setLastDeleteOrphans(loadLastRun(ACTION_DELETE_ORPHANS));
  }, []);

  // ---- Derived ----------------------------------------------------------
  const failurePatternList = failurePattern
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  // ---- Automated source cleanup -----------------------------------------
  const doSourceCleanup = useCallback(async (execute: boolean) => {
    setRunning(true);
    setOpKind('source-cleanup');
    setOpStartedAt(Date.now());
    setOpError(null);
    setCleanupResult(null);
    try {
      setCleanupResult(execute ? await runSourceCleanup() : await previewSourceCleanup());
    } catch (error: any) {
      setOpError(error?.message || 'Source cleanup failed');
    } finally {
      setRunning(false);
      setOpStartedAt(null);
    }
  }, []);

  // ---- Summary loader ----------------------------------------------------
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const data = await getWorkflowSummary({
        startTime: localInputToIso(startTime),
        endTime: localInputToIso(endTime),
        failurePattern: failurePattern || undefined,
      });
      setSummary(data);
      setSummaryLoadedAt(new Date());
    } catch (e: any) {
      setSummaryError(e?.message ?? 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [startTime, endTime, failurePattern]);

  useEffect(() => {
    loadSummary();
    // Only on first mount; user explicitly re-loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Lookup loader -----------------------------------------------------
  const runLookup = async () => {
    const idNum = lookupSourceId.trim() ? Number(lookupSourceId.trim()) : undefined;
    if (idNum != null && (!Number.isFinite(idNum) || idNum <= 0)) {
      setLookupError('eventSourceId must be a positive integer.');
      return;
    }
    if (!idNum && !lookupSource.trim()) {
      setLookupError('Provide an eventSourceId or a source substring.');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setExpandedRow(null);
    try {
      const data = await lookupWorkflowRuns({
        eventSourceId: idNum,
        source: lookupSource.trim() || undefined,
        allTime: lookupAllTime,
        startTime: localInputToIso(startTime),
        endTime: localInputToIso(endTime),
      });
      setLookup(data);
    } catch (e: any) {
      setLookupError(e?.message ?? 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  // ---- Orphan loader -----------------------------------------------------
  const loadOrphans = useCallback(async () => {
    setOrphansLoading(true);
    setOrphansError(null);
    try {
      const data = await getOrphanEntities();
      setOrphans(data);
    } catch (e: any) {
      setOrphansError(e?.message ?? 'Failed to load orphan entities');
    } finally {
      setOrphansLoading(false);
    }
  }, []);

  // ---- Mutations ---------------------------------------------------------
  const doDeleteSource = async (
    eventSourceId: number,
    sourceLabel?: string,
  ) => {
    setDialog(null);
    setRunning(true);
    setOpKind('delete-source');
    setOpError(null);
    setDeleteSourceResult(null);
    setOpStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const data = await deleteEventSourceCascade(eventSourceId);
      setDeleteSourceResult(data);
      const record: LastRunRecord<WorkflowDeleteSourceResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Math.round(performance.now() - t0),
        summary: {
          eventSourceId,
          sourceLabel: sourceLabel ?? data.sourceUrl ?? '',
          found: data.found,
          ...(data.deleted ?? {}),
        },
        response: data,
      };
      saveLastRun(ACTION_DELETE_SOURCE, record);
      setLastDeleteSource(record);
      // Refresh affected views
      await Promise.all([loadSummary(), orphans ? loadOrphans() : Promise.resolve()]);
      // If lookup was on this source, refresh too.
      if (
        lookup &&
        lookup.records.some((r) => r.EventSourceId === eventSourceId)
      ) {
        // remove rows referencing the deleted source from the visible list
        setLookup({
          ...lookup,
          count: lookup.records.filter(
            (r) => r.EventSourceId !== eventSourceId,
          ).length,
          records: lookup.records.filter(
            (r) => r.EventSourceId !== eventSourceId,
          ),
        });
      }
    } catch (e: any) {
      setOpError(e?.message ?? 'Delete failed');
    } finally {
      setRunning(false);
      setOpStartedAt(null);
    }
  };

  const doDeleteFailed = async () => {
    setDialog(null);
    setRunning(true);
    setOpKind('delete-failed');
    setOpError(null);
    setDeleteFailedResult(null);
    setOpStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const data = await deleteFailedSources({
        startTime: localInputToIso(startTime),
        endTime: localInputToIso(endTime),
        failurePattern: failurePattern || undefined,
      });
      setDeleteFailedResult(data);
      const record: LastRunRecord<WorkflowDeleteFailedResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Math.round(performance.now() - t0),
        summary: {
          candidatesFound: data.candidatesFound,
          ...(data.deletedTotals ?? {}),
          patterns: data.failureDetection?.patterns?.join(',') ?? '',
        },
        response: data,
      };
      saveLastRun(ACTION_DELETE_FAILED, record);
      setLastDeleteFailed(record);
      await loadSummary();
    } catch (e: any) {
      setOpError(e?.message ?? 'Delete failed');
    } finally {
      setRunning(false);
      setOpStartedAt(null);
    }
  };

  const doDeleteOrphans = async () => {
    setDialog(null);
    setRunning(true);
    setOpKind('delete-orphans');
    setOpError(null);
    setDeleteOrphansResult(null);
    setOpStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const data = await deleteOrphanEntities();
      setDeleteOrphansResult(data);
      const record: LastRunRecord<WorkflowDeleteOrphanEntitiesResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Math.round(performance.now() - t0),
        summary: { deletedCount: data.deletedCount },
        response: data,
      };
      saveLastRun(ACTION_DELETE_ORPHANS, record);
      setLastDeleteOrphans(record);
      // Clear orphan list and refresh summary
      setOrphans({
        action: 'orphan-entities',
        count: 0,
        entities: [],
      });
      await loadSummary();
    } catch (e: any) {
      setOpError(e?.message ?? 'Delete failed');
    } finally {
      setRunning(false);
      setOpStartedAt(null);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const failureRunCount =
    summary?.statusCounts
      ?.filter((s) => isFailureStatus(s.status, failurePatternList))
      ?.reduce((a, b) => a + b.count, 0) ?? 0;

  return (
    <AdminLayout
      currentPage="workflowMetadata"
      pageTitle="Workflow Runs & Source Cleanup"
      pageDescription="Audit WorkflowMetadata, look up failed runs, and cascade-delete EventSources or orphaned Entities."
    >
      {/* ---------------------------- Window controls ---------------------- */}
      <Card>
        <CardTitle>Time window &amp; failure detection</CardTitle>
        <CardDescription>
          Applied to the Summary card and the <code>delete-failed</code>{' '}
          action. Lookup uses these too unless you tick "Search all time".
        </CardDescription>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          <Field>
            <Label>Start time (local)</Label>
            <Input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={running}
            />
          </Field>
          <Field>
            <Label>End time (local)</Label>
            <Input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={running}
            />
          </Field>
          <Field>
            <Label>
              Failure pattern{' '}
              <span style={{ fontWeight: 400, color: '#8c8c8c' }}>
                (comma-separated, case-insensitive)
              </span>
            </Label>
            <Input
              type="text"
              value={failurePattern}
              onChange={(e) => setFailurePattern(e.target.value)}
              placeholder="fail,error,timeout"
              disabled={running}
            />
          </Field>
        </div>

        <ButtonRow>
          <SecondaryButton onClick={() => setQuickRange(1)} disabled={running}>
            Last 24h
          </SecondaryButton>
          <SecondaryButton onClick={() => setQuickRange(3)} disabled={running}>
            Last 3d (default)
          </SecondaryButton>
          <SecondaryButton onClick={() => setQuickRange(7)} disabled={running}>
            Last 7d
          </SecondaryButton>
          <SecondaryButton onClick={() => setQuickRange(30)} disabled={running}>
            Last 30d
          </SecondaryButton>
          <PrimaryButton onClick={loadSummary} disabled={summaryLoading || running}>
            <RefreshCw size={16} />
            {summaryLoading ? 'Loading…' : 'Apply &amp; refresh summary'}
          </PrimaryButton>
        </ButtonRow>
      </Card>

      {/* ---------------------------- Summary ------------------------------ */}
      <Card>
        <CardTitle>Summary</CardTitle>
        <CardDescription>
          Aggregate stats from <code>WorkflowMetadata</code> over the selected
          time window.
        </CardDescription>

        {summaryError && <Banner tone="error">{summaryError}</Banner>}
        {summaryLoading && (
          <RunningIndicator
            startedAt={Date.now()}
            message="Loading workflow summary…"
          />
        )}

        {summary && (
          <>
            <StatGrid>
              <Metric
                label="Total runs"
                value={summary.totalCount.toLocaleString()}
              />
              <Metric
                label="Failed runs"
                hint={`patterns: ${(summary.failureDetection?.patterns ?? []).join(', ') || '—'}`}
                value={failureRunCount.toLocaleString()}
                tone={failureRunCount > 0 ? 'warning' : 'success'}
              />
              <Metric
                label="Orphan entities"
                value={summary.orphanEntityCount.toLocaleString()}
                tone={summary.orphanEntityCount > 0 ? 'warning' : 'success'}
              />
              <Metric
                label="Coverage avg / run"
                hint="AVG(TotalNewEvents / TotalEvents)"
                value={
                  summary.eventCoverage?.runsConsidered
                    ? `${(summary.eventCoverage.perRunAverageRatio * 100).toFixed(1)}%`
                    : '—'
                }
              />
              <Metric
                label="Coverage overall"
                hint="SUM(NewEvents) / SUM(Events)"
                value={
                  summary.eventCoverage?.sumTotalEvents
                    ? `${(summary.eventCoverage.overallRatio * 100).toFixed(1)}%`
                    : '—'
                }
              />
              <Metric
                label="Runs considered (coverage)"
                value={(summary.eventCoverage?.runsConsidered ?? 0).toLocaleString()}
              />
            </StatGrid>

            {summary.statusCounts.length > 0 && (
              <>
                <CardTitle style={{ fontSize: 16, marginTop: 16 }}>
                  Status breakdown
                </CardTitle>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th style={{ width: 120 }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.statusCounts.map((s) => {
                      const failure = isFailureStatus(s.status, failurePatternList);
                      return (
                        <tr
                          key={s.status}
                          style={{
                            background: failure ? '#fff1f0' : undefined,
                          }}
                        >
                          <td>
                            {failure && (
                              <AlertTriangle
                                size={14}
                                style={{ verticalAlign: 'middle', color: '#cf1322', marginRight: 6 }}
                              />
                            )}
                            {s.status || '(empty)'}
                          </td>
                          <td>{s.count.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </SampleTable>
              </>
            )}

            {(summary.stageCounts.length > 0 ||
              summary.failureStageCounts.length > 0) && (
              <>
                <CardTitle style={{ fontSize: 16, marginTop: 16 }}>
                  Stage breakdown
                </CardTitle>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th style={{ width: 100 }}>All</th>
                      <th style={{ width: 100 }}>Failures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const allStages = new Set<string>();
                      summary.stageCounts.forEach((s) => allStages.add(s.stage));
                      summary.failureStageCounts.forEach((s) =>
                        allStages.add(s.stage),
                      );
                      const rows = Array.from(allStages).map((stage) => ({
                        stage,
                        all:
                          summary.stageCounts.find((s) => s.stage === stage)
                            ?.count ?? 0,
                        fail:
                          summary.failureStageCounts.find(
                            (s) => s.stage === stage,
                          )?.count ?? 0,
                      }));
                      rows.sort((a, b) => b.all - a.all);
                      return rows.map((r) => (
                        <tr key={r.stage}>
                          <td>{r.stage || '(empty)'}</td>
                          <td>{r.all.toLocaleString()}</td>
                          <td
                            style={{
                              color: r.fail > 0 ? '#cf1322' : undefined,
                              fontWeight: r.fail > 0 ? 600 : 400,
                            }}
                          >
                            {r.fail.toLocaleString()}
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </SampleTable>
              </>
            )}
          </>
        )}

        <ButtonRow>
          <PrimaryButton onClick={loadSummary} disabled={summaryLoading || running}>
            <RefreshCw size={16} />
            {summaryLoading ? 'Refreshing…' : 'Refresh'}
          </PrimaryButton>
          {summaryLoadedAt && !summaryLoading && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Loaded {summaryLoadedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
      </Card>

      {/* ---------------------------- Lookup ------------------------------- */}
      <Card>
        <CardTitle>Lookup workflow runs</CardTitle>
        <CardDescription>
          Search by exact <code>EventSourceId</code> or by a substring of the{' '}
          <code>Source</code> URL. Returns up to 500 matching runs joined with{' '}
          <code>EventSources</code> and <code>EventSourceTypes</code>.
        </CardDescription>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          <Field>
            <Label>EventSourceId</Label>
            <Input
              type="number"
              min={1}
              value={lookupSourceId}
              onChange={(e) => setLookupSourceId(e.target.value)}
              placeholder="e.g. 42"
              disabled={lookupLoading || running}
            />
          </Field>
          <Field>
            <Label>Source contains</Label>
            <Input
              type="text"
              value={lookupSource}
              onChange={(e) => setLookupSource(e.target.value)}
              placeholder="e.g. contoso.com"
              disabled={lookupLoading || running}
            />
          </Field>
          <Field>
            <Label>&nbsp;</Label>
            <InlineLabel>
              <input
                type="checkbox"
                checked={lookupAllTime}
                onChange={(e) => setLookupAllTime(e.target.checked)}
                disabled={lookupLoading || running}
                style={{ marginRight: 8 }}
              />
              Search all time (ignore window)
            </InlineLabel>
          </Field>
        </div>

        <ButtonRow>
          <PrimaryButton onClick={runLookup} disabled={lookupLoading || running}>
            <Search size={16} />
            {lookupLoading ? 'Searching…' : 'Run lookup'}
          </PrimaryButton>
          {lookup && !lookupLoading && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              {lookup.count.toLocaleString()} record{lookup.count === 1 ? '' : 's'} returned
            </span>
          )}
        </ButtonRow>

        {lookupError && <Banner tone="error">{lookupError}</Banner>}

        {lookup && lookup.records.length === 0 && !lookupError && (
          <Banner tone="info">No workflow runs matched.</Banner>
        )}

        {lookup && lookup.records.length > 0 && (
          <SampleTable>
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Run Id</th>
                <th>Source Id</th>
                <th>Source</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Start</th>
                <th>New / Total</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {lookup.records.map((r) => {
                const failure = isFailureStatus(r.Status, failurePatternList);
                const open = expandedRow === r.Id;
                return (
                  <React.Fragment key={r.Id}>
                    <tr style={{ background: failure ? '#fff1f0' : undefined }}>
                      <td>
                        <button
                          onClick={() => setExpandedRow(open ? null : r.Id)}
                          aria-label={open ? 'Collapse' : 'Expand'}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </td>
                      <td>{r.Id}</td>
                      <td>{r.EventSourceId}</td>
                      <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.EventSourceUrl ? (
                          <a href={r.EventSourceUrl} target="_blank" rel="noopener noreferrer">
                            {r.Source ?? r.EventSourceUrl}
                          </a>
                        ) : (
                          r.Source ?? '—'
                        )}
                      </td>
                      <td>{r.Stage ?? '—'}</td>
                      <td style={{ color: failure ? '#cf1322' : undefined, fontWeight: failure ? 600 : 400 }}>
                        {r.Status ?? '—'}
                      </td>
                      <td>{fmtDate(r.StartTime)}</td>
                      <td>
                        {(r.TotalNewEvents ?? 0).toLocaleString()} /{' '}
                        {(r.TotalEvents ?? 0).toLocaleString()}
                      </td>
                      <td>
                        <DangerButton
                          onClick={() =>
                            setDialog({
                              kind: 'delete-source',
                              eventSourceId: r.EventSourceId,
                              sourceLabel: r.EventSourceUrl ?? r.Source,
                            })
                          }
                          disabled={running}
                          style={{ padding: '6px 10px', fontSize: 12 }}
                        >
                          <Trash2 size={14} />
                          Cascade delete
                        </DangerButton>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={9} style={{ background: '#fafafa' }}>
                          <RawJson data={r} label={`Run ${r.Id}`} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </SampleTable>
        )}
      </Card>

      {/* -------------------- Cascade delete by EventSourceId -------------- */}
      <Card>
        <CardTitle>Cascade-delete an EventSource</CardTitle>
        <CardDescription>
          Removes the source and all rows that reference it:{' '}
          <code>WorkflowMetadata</code>, <code>EntityEventSources</code>,{' '}
          <code>EventInfoMap</code>, then <code>EventSources</code>.{' '}
          <strong>EventInfo rows are preserved.</strong>
        </CardDescription>

        <Field>
          <Label>EventSourceId</Label>
          <Input
            type="number"
            min={1}
            value={deleteIdInput}
            onChange={(e) => setDeleteIdInput(e.target.value)}
            placeholder="e.g. 42"
            disabled={running}
          />
        </Field>

        {running && opKind === 'delete-source' && opStartedAt != null && (
          <RunningIndicator
            startedAt={opStartedAt}
            message="Cascade-deleting EventSource…"
          />
        )}

        {opKind === 'delete-source' && opError && (
          <Banner tone="error">{opError}</Banner>
        )}

        {opKind === 'delete-source' && deleteSourceResult && !opError && (
          <>
            {deleteSourceResult.found ? (
              <Banner tone="success">
                Deleted EventSource {deleteSourceResult.eventSourceId}
                {deleteSourceResult.sourceUrl
                  ? ` (${deleteSourceResult.sourceUrl})`
                  : ''}
                .
              </Banner>
            ) : (
              <Banner tone="warning">
                {deleteSourceResult.message ??
                  `EventSource ${deleteSourceResult.eventSourceId} does not exist.`}
              </Banner>
            )}
            {deleteSourceResult.deleted && (
              <StatGrid>
                <Metric
                  label="EventSources"
                  value={(deleteSourceResult.deleted.eventSources ?? 0).toLocaleString()}
                />
                <Metric
                  label="WorkflowMetadata"
                  value={(deleteSourceResult.deleted.workflowMetadata ?? 0).toLocaleString()}
                />
                <Metric
                  label="EntityEventSources"
                  value={(deleteSourceResult.deleted.entityEventSources ?? 0).toLocaleString()}
                />
                <Metric
                  label="EventInfoMap"
                  value={(deleteSourceResult.deleted.eventInfoMap ?? 0).toLocaleString()}
                />
              </StatGrid>
            )}
            <RawJson data={deleteSourceResult} />
          </>
        )}

        <ButtonRow>
          <DangerButton
            onClick={() => {
              const id = Number(deleteIdInput.trim());
              if (!Number.isFinite(id) || id <= 0) return;
              setDialog({ kind: 'delete-source', eventSourceId: id });
            }}
            disabled={
              running ||
              !deleteIdInput.trim() ||
              !Number.isFinite(Number(deleteIdInput.trim())) ||
              Number(deleteIdInput.trim()) <= 0
            }
          >
            <Trash2 size={16} />
            Cascade delete this source
          </DangerButton>
        </ButtonRow>
      </Card>

      <LastRunCard record={lastDeleteSource} />

      {/* ------------------- Automated source cleanup ---------------------- */}
      <Card>
        <CardTitle>Automated source cleanup (weekly)</CardTitle>
        <CardDescription>
          Runs automatically at the start of the <strong>WebIQ refresh night</strong>,
          before the refresh, so dead sources are retired before WebIQ can
          rediscover them and before the night&apos;s extraction budget is spent
          on them.
          <br />
          <br />
          A source is retired when it has{' '}
          <strong>{cleanupResult?.policy.minConsecutiveFailures ?? 3}+ consecutive
          extraction failures</strong> and has not succeeded in{' '}
          <strong>{cleanupResult?.policy.successGraceDays ?? 30} days</strong>, or
          when it fetches cleanly but carries a{' '}
          <strong>structural defect</strong> that means it can never yield
          events — a URL pinned to a past year, a staging or sandbox host, a
          malformed URL, a single press release rather than a listing, a
          redirect to a different site, or a body that reports the page does not
          exist.
          <br />
          <br />
          Retiring sets <code>LifecycleStatus = RETIRED</code> and deactivates the
          mappings — nothing is deleted, and both the history and the row remain
          for review. Sources with any <strong>manual</strong> mapping are never
          touched.
          <br />
          <br />
          Sources that extract cleanly, have produced no new events, and show{' '}
          <strong>no identifiable defect</strong> are{' '}
          <strong>reported, not retired</strong>: a valid page with nothing
          upcoming is a successful extraction, and the cost gate already skips
          unchanged pages. Bot challenges and login walls are reported too, since
          they can be transient.
        </CardDescription>

        {running && opKind === 'source-cleanup' && opStartedAt != null && (
          <RunningIndicator startedAt={opStartedAt} message="Running source cleanup…" />
        )}

        {opKind === 'source-cleanup' && opError && <Banner tone="error">{opError}</Banner>}

        {opKind === 'source-cleanup' && cleanupResult && !opError && (
          <>
            {cleanupResult.policy.dryRun ? (
              <Banner tone="info">
                Preview only — nothing was changed.
              </Banner>
            ) : (
              <Banner tone="success">
                Retired {cleanupResult.retiredCount.toLocaleString()} source
                {cleanupResult.retiredCount === 1 ? '' : 's'} and deactivated{' '}
                {cleanupResult.mappingsDeactivated.toLocaleString()} mapping
                {cleanupResult.mappingsDeactivated === 1 ? '' : 's'}.
              </Banner>
            )}

            {cleanupResult.skipped && (
              <Banner tone={cleanupResult.skipped.reason === 'outage_suspected' ? 'error' : 'warning'}>
                {cleanupResult.skipped.detail}
              </Banner>
            )}

            <StatGrid>
              <Metric
                label="Active sources"
                value={cleanupResult.activeSources.toLocaleString()}
              />
              <Metric
                label="Failing repeatedly"
                value={(cleanupResult.failingCount ?? 0).toLocaleString()}
                tone={(cleanupResult.failingCount ?? 0) > 0 ? 'warning' : undefined}
              />
              <Metric
                label="Structurally defective"
                value={(cleanupResult.defectiveCount ?? 0).toLocaleString()}
                tone={(cleanupResult.defectiveCount ?? 0) > 0 ? 'warning' : undefined}
              />
              <Metric
                label="Retired"
                value={cleanupResult.retiredCount.toLocaleString()}
              />
              <Metric
                label="Flagged for review"
                value={cleanupResult.flaggedForReview.count.toLocaleString()}
              />
            </StatGrid>

            {Object.keys(cleanupResult.defectCounts ?? {}).length > 0 && (
              <>
                <CardDescription>Defects found:</CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Defect</th>
                      <th>Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(cleanupResult.defectCounts).map(([reason, count]) => (
                      <tr key={reason}>
                        <td>{reason.replace(/_/g, ' ')}</td>
                        <td>{count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {(cleanupResult.defective ?? []).length > 0 && (
              <>
                <CardDescription>
                  {cleanupResult.policy.dryRun ? 'Would retire' : 'Retired'} for a
                  structural defect:
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>EventSourceId</th>
                      <th>Source</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupResult.defective.map((item) => (
                      <tr key={item.eventSourceId}>
                        <td>{item.eventSourceId}</td>
                        <td>{item.source ?? '—'}</td>
                        <td>{item.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {cleanupResult.retired.length > 0 && (
              <>
                <CardDescription>
                  {cleanupResult.policy.dryRun ? 'Would retire' : 'Retired'}:
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>EventSourceId</th>
                      <th>Source</th>
                      <th>Failures</th>
                      <th>Last success</th>
                      <th>Last stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupResult.retired.map((item) => (
                      <tr key={item.eventSourceId}>
                        <td>{item.eventSourceId}</td>
                        <td>{item.source ?? '—'}</td>
                        <td>{item.consecutiveFailures}</td>
                        <td>{fmtDate(item.lastSuccessfulExtractionAt ?? undefined)}</td>
                        <td>{item.lastStage ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {cleanupResult.flaggedForReview.sources.length > 0 && (
              <>
                <CardDescription>
                  Working, but no new events in the last{' '}
                  {cleanupResult.flaggedForReview.quietDays} days and no defect
                  I can identify. Not retired — review these yourself.
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>EventSourceId</th>
                      <th>Source</th>
                      <th>Events extracted (all time)</th>
                      <th>Last success</th>
                      <th>Observations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupResult.flaggedForReview.sources.map((item) => (
                      <tr key={item.eventSourceId}>
                        <td>{item.eventSourceId}</td>
                        <td>{item.source ?? '—'}</td>
                        <td>{item.eventsExtractedCount.toLocaleString()}</td>
                        <td>{fmtDate(item.lastSuccessfulExtractionAt ?? undefined)}</td>
                        <td>{(item.observations ?? []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            <RawJson data={cleanupResult} />
          </>
        )}

        <ButtonRow>
          <SecondaryButton onClick={() => doSourceCleanup(false)} disabled={running}>
            Preview cleanup
          </SecondaryButton>
          <PrimaryButton onClick={() => doSourceCleanup(true)} disabled={running}>
            Run cleanup now
          </PrimaryButton>
        </ButtonRow>
      </Card>

      {/* ---------------------- Delete failed sources ---------------------- */}
      <Card>
        <CardTitle>Delete sources whose latest run failed</CardTitle>
        <CardDescription>
          <strong>Irreversible.</strong> Prefer the automated cleanup above,
          which retires rather than deletes and can be undone. Use this only to
          purge sources you are certain should never come back.
          <br />
          <br />
          For each <code>EventSourceId</code> in the time window, considers the{' '}
          <strong>most recent</strong> <code>WorkflowMetadata</code> row. If
          its <code>Status</code> matches a failure pattern, that{' '}
          <code>EventSource</code> is cascade-deleted (same order as the action
          above). Sources that recovered after a failure are{' '}
          <strong>preserved</strong>.
        </CardDescription>

        <Banner tone="info">
          Window: <code>{startTime || '—'}</code> → <code>{endTime || '—'}</code>{' '}
          · Patterns: <code>{failurePattern || '(none)'}</code>
        </Banner>

        {running && opKind === 'delete-failed' && opStartedAt != null && (
          <RunningIndicator
            startedAt={opStartedAt}
            message="Deleting failed sources…"
          />
        )}

        {opKind === 'delete-failed' && opError && (
          <Banner tone="error">{opError}</Banner>
        )}

        {opKind === 'delete-failed' && deleteFailedResult && !opError && (
          <>
            <Banner tone="success">
              Processed {deleteFailedResult.candidatesFound.toLocaleString()}{' '}
              candidate source
              {deleteFailedResult.candidatesFound === 1 ? '' : 's'}.
            </Banner>
            <StatGrid>
              <Metric
                label="EventSources deleted"
                value={(deleteFailedResult.deletedTotals.eventSources ?? 0).toLocaleString()}
                tone="warning"
              />
              <Metric
                label="WorkflowMetadata"
                value={(deleteFailedResult.deletedTotals.workflowMetadata ?? 0).toLocaleString()}
              />
              <Metric
                label="EntityEventSources"
                value={(deleteFailedResult.deletedTotals.entityEventSources ?? 0).toLocaleString()}
              />
              <Metric
                label="EventInfoMap"
                value={(deleteFailedResult.deletedTotals.eventInfoMap ?? 0).toLocaleString()}
              />
            </StatGrid>

            {deleteFailedResult.deleted.length > 0 && (
              <SampleTable>
                <thead>
                  <tr>
                    <th>EventSourceId</th>
                    <th>Last status</th>
                    <th>Last stage</th>
                    <th>Last start</th>
                  </tr>
                </thead>
                <tbody>
                  {deleteFailedResult.deleted.map((d) => (
                    <tr key={d.eventSourceId}>
                      <td>{d.eventSourceId}</td>
                      <td>{d.lastStatus ?? '—'}</td>
                      <td>{d.lastStage ?? '—'}</td>
                      <td>{fmtDate(d.lastStartTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            )}
            <RawJson data={deleteFailedResult} />
          </>
        )}

        <ButtonRow>
          <DangerButton
            onClick={() => setDialog({ kind: 'delete-failed' })}
            disabled={running}
          >
            <Trash2 size={16} />
            Delete failed sources in window
          </DangerButton>
        </ButtonRow>
      </Card>

      <LastRunCard record={lastDeleteFailed} />

      {/* ---------------------- Orphan entities ---------------------------- */}
      <Card>
        <CardTitle>Orphan entities</CardTitle>
        <CardDescription>
          Entities in the <code>Entities</code> table with{' '}
          <strong>no</strong> <code>EntityEventSources</code> rows. The summary
          above shows the count{' '}
          {summary && (
            <>
              (<strong>{summary.orphanEntityCount.toLocaleString()}</strong>)
            </>
          )}
          ; load the list below before deleting so you can review them first.
        </CardDescription>

        <ButtonRow>
          <PrimaryButton onClick={loadOrphans} disabled={orphansLoading || running}>
            <RefreshCw size={16} />
            {orphansLoading ? 'Loading…' : orphans ? 'Reload' : 'Load orphan entities'}
          </PrimaryButton>
          <DangerButton
            onClick={() => setDialog({ kind: 'delete-orphans' })}
            disabled={running || (orphans?.count ?? summary?.orphanEntityCount ?? 0) === 0}
          >
            <Trash2 size={16} />
            Delete all orphan entities
          </DangerButton>
        </ButtonRow>

        {orphansError && <Banner tone="error">{orphansError}</Banner>}

        {orphans && orphans.entities.length === 0 && !orphansError && (
          <Banner tone="success">No orphan entities. 🎉</Banner>
        )}

        {orphans && orphans.entities.length > 0 && (
          <SampleTable>
            <thead>
              <tr>
                <th>Id</th>
                <th>Entity name</th>
                <th>Type</th>
                <th>OneId</th>
                <th>Contact</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orphans.entities.map((e) => (
                <tr key={e.Id}>
                  <td>{e.Id}</td>
                  <td>{e.EntityName ?? '—'}</td>
                  <td>{e.EntityTypeId ?? '—'}</td>
                  <td>{e.OneId ?? '—'}</td>
                  <td>{e.EntityContact ?? '—'}</td>
                  <td>{fmtDate(e.DateCreated)}</td>
                </tr>
              ))}
            </tbody>
          </SampleTable>
        )}

        {running && opKind === 'delete-orphans' && opStartedAt != null && (
          <RunningIndicator
            startedAt={opStartedAt}
            message="Deleting orphan entities…"
          />
        )}

        {opKind === 'delete-orphans' && opError && (
          <Banner tone="error">{opError}</Banner>
        )}

        {opKind === 'delete-orphans' && deleteOrphansResult && !opError && (
          <>
            <Banner tone="success">
              Deleted {deleteOrphansResult.deletedCount.toLocaleString()} orphan
              entit{deleteOrphansResult.deletedCount === 1 ? 'y' : 'ies'}.
            </Banner>
            <RawJson data={deleteOrphansResult} />
          </>
        )}
      </Card>

      <LastRunCard record={lastDeleteOrphans} />

      {/* ---------------------------- Dialogs ------------------------------ */}
      <ConfirmDialog
        open={dialog?.kind === 'delete-source'}
        title="Cascade-delete EventSource"
        body={
          dialog?.kind === 'delete-source' ? (
            <>
              <p>
                You're about to cascade-delete{' '}
                <strong>EventSource {dialog.eventSourceId}</strong>
                {dialog.sourceLabel ? (
                  <>
                    {' '}(<code>{dialog.sourceLabel}</code>)
                  </>
                ) : null}
                .
              </p>
              <p>
                Rows in <code>WorkflowMetadata</code>,{' '}
                <code>EntityEventSources</code>, and{' '}
                <code>EventInfoMap</code> referencing this source will be
                removed first, then the <code>EventSources</code> row itself.{' '}
                <code>EventInfo</code> rows are preserved.
              </p>
              <p>This <strong>cannot be undone</strong>.</p>
            </>
          ) : null
        }
        promptText={<>Type <code>DELETE</code> to confirm:</>}
        confirmPhrase="DELETE"
        primaryLabel="Cascade delete"
        onConfirm={() =>
          dialog?.kind === 'delete-source' &&
          doDeleteSource(dialog.eventSourceId, dialog.sourceLabel)
        }
        onCancel={() => setDialog(null)}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete-failed'}
        title="Delete failed sources in window"
        body={
          <>
            <p>
              You're about to delete every <code>EventSource</code> whose{' '}
              <strong>most recent</strong> <code>WorkflowMetadata</code> row in
              the current time window has a status matching one of the failure
              patterns.
            </p>
            <ul>
              <li>
                Window: <code>{startTime || '—'}</code> →{' '}
                <code>{endTime || '—'}</code>
              </li>
              <li>
                Patterns: <code>{failurePattern || '(none)'}</code>
              </li>
            </ul>
            <p>This <strong>cannot be undone</strong>.</p>
          </>
        }
        promptText={<>Type <code>DELETE FAILED</code> to confirm:</>}
        confirmPhrase="DELETE FAILED"
        primaryLabel="Delete failed sources"
        onConfirm={doDeleteFailed}
        onCancel={() => setDialog(null)}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete-orphans'}
        title="Delete all orphan entities"
        body={
          <>
            <p>
              You're about to delete{' '}
              <strong>
                {(orphans?.count ?? summary?.orphanEntityCount ?? 0).toLocaleString()}
              </strong>{' '}
              <code>Entity</code> row{(orphans?.count ?? summary?.orphanEntityCount ?? 0) === 1 ? '' : 's'} that have no
              remaining <code>EntityEventSources</code>.
            </p>
            <p>This <strong>cannot be undone</strong>.</p>
          </>
        }
        promptText={<>Type <code>DELETE ORPHANS</code> to confirm:</>}
        confirmPhrase="DELETE ORPHANS"
        primaryLabel="Delete orphan entities"
        onConfirm={doDeleteOrphans}
        onCancel={() => setDialog(null)}
      />
    </AdminLayout>
  );
};

export default AdminWorkflowMetadataPage;
